import { useMemo, useState } from "react";
import { api } from "../api";
import type { Customer } from "../types";
import { Modal } from "./Modal";
import { localDatetimeToIso, nowLocalDatetime, plusDaysLocalDatetime, signDialogDefaults } from "../utils";

export function SignDialog({
  customer,
  onClose,
  onSigned,
  flash,
}: {
  customer: Customer;
  onClose: () => void;
  onSigned: () => Promise<void>;
  flash: (kind: "info" | "error", msg: string) => void;
}) {
  const [maxTemplates, setMaxTemplates] = useState(() => signDialogDefaults(customer).maxTemplates);
  const [days, setDays] = useState(() => signDialogDefaults(customer).days);
  const [notBefore, setNotBefore] = useState("");
  const [notAfter, setNotAfter] = useState("");
  const [userNote, setUserNote] = useState("");
  const [issuer, setIssuer] = useState("");
  const [fingerprint, setFingerprint] = useState(customer.fingerprint ?? "");
  const [busy, setBusy] = useState(false);

  const nowPlaceholder = useMemo(() => nowLocalDatetime(), []);
  const defaultNotAfter = useMemo(() => plusDaysLocalDatetime(days), [days]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fingerprint.trim()) {
      flash("error", "请填写硬件指纹码（也可以先在客户表单里存好）");
      return;
    }
    setBusy(true);
    try {
      const result = await api.sign(customer.id, {
        max_templates: Number(maxTemplates),
        days: notAfter ? null : Number(days),
        not_before: notBefore ? localDatetimeToIso(notBefore) : null,
        not_after: notAfter ? localDatetimeToIso(notAfter) : null,
        user_note: userNote || null,
        issuer: issuer || null,
        fingerprint: fingerprint.trim(),
      });
      flash("info", `已签发：${result.license.file}`);
      await onSigned();
    } catch (err: unknown) {
      flash("error", (err as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal title={`签发 license：${customer.name}`} onClose={onClose} size="lg">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="label">硬件指纹码 *</label>
          <input
            className="input font-mono text-xs"
            required
            value={fingerprint}
            onChange={(e) => setFingerprint(e.target.value)}
            placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
          />
          <p className="mt-1 text-xs text-ink-500">
            会同步写回到客户档案的「主指纹」字段（与签发记录绑定）。
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label">可新建自定义频道数 *</label>
            <input
              className="input"
              type="number"
              min={0}
              required
              value={maxTemplates}
              onChange={(e) => setMaxTemplates(Number(e.target.value))}
            />
            <p className="mt-1 text-xs text-ink-500">
              客户能再额外建几个「自定义频道」。<br />
              内置 2 个默认频道（单人讲故事 / 双人讲故事）不计入；
              <strong>0</strong> = 客户只能用内置 2 个、不能再新建。
            </p>
          </div>
          <div>
            <label className="label">授权天数</label>
            <input
              className="input"
              type="number"
              min={1}
              value={days}
              disabled={!!notAfter}
              onChange={(e) => setDays(Number(e.target.value))}
            />
          </div>
          <div>
            <label className="label">签发人 issuer</label>
            <input
              className="input"
              value={issuer}
              onChange={(e) => setIssuer(e.target.value)}
              placeholder="默认 lixiaoyaoshuoshuo"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">生效起始（可空 = 现在）</label>
            <div className="flex gap-2">
              <input
                className="input"
                type="datetime-local"
                value={notBefore}
                onChange={(e) => setNotBefore(e.target.value)}
              />
              <button
                type="button"
                className="btn-secondary whitespace-nowrap"
                onClick={() => setNotBefore("")}
                title="清空，使用当前时间"
              >
                清空
              </button>
            </div>
            <p className="mt-1 text-xs text-ink-500">
              留空表示「现在」（{nowPlaceholder}），时间按本机时区。
            </p>
          </div>
          <div>
            <label className="label">截止时间（可空；填了会忽略「天数」）</label>
            <div className="flex gap-2">
              <input
                className="input"
                type="datetime-local"
                value={notAfter}
                onChange={(e) => setNotAfter(e.target.value)}
              />
              <button
                type="button"
                className="btn-secondary whitespace-nowrap"
                onClick={() => setNotAfter(defaultNotAfter)}
                title={`快速填入：${defaultNotAfter}`}
              >
                +{days}天
              </button>
              <button
                type="button"
                className="btn-secondary whitespace-nowrap"
                onClick={() => setNotAfter("")}
                title="清空"
              >
                清空
              </button>
            </div>
            <p className="mt-1 text-xs text-ink-500">
              留空表示按上方的「授权天数」推算。
            </p>
          </div>
        </div>
        <div>
          <label className="label">用户备注</label>
          <input
            className="input"
            value={userNote}
            onChange={(e) => setUserNote(e.target.value)}
            placeholder="不填会自动用「客户姓名 + 微信号」拼"
          />
        </div>
        <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
          ⚠️ 签发后 license.dat 会同时写到：
          <code className="ml-1 font-mono">issued/&lt;date&gt;-{customer.id}-&lt;serial&gt;.dat</code>
          （本地留底）和
          <code className="ml-1 font-mono">customers/{customer.id}/licenses/...</code>
          （可提交 git）。
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            取消
          </button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? "签发中…" : "确认签发"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
